import * as React from "react";
import PasswordStrengthBar, {
  PasswordStrengthBarProps,
} from "react-password-strength-bar";
import styled from "styled-components";

import Style from "@client/utils/Style";

import closeImage from "./images/close.svg";

const Wrapper = styled.div`
  position: relative;
`;
const getInputWidth = (props: any) => {
  if (props.width) {
    return props.width;
  }

  if (props.large) {
    return `${Style.inputSizes.large.width}px`;
  }

  return `${Style.inputSizes.regular.width}px`;
};
const borderColor = (props: any, defaultColor = Style.palette.grayscale[3]) =>
  props.highlight ? Style.palette.alert : defaultColor;
const Input = styled.input`
  width: ${props => getInputWidth(props)};
  height: ${(props: any) =>
    props.height || `${Style.inputSizes.regular.height}px`};
  line-height: ${(props: any) => props.lineHeight || "normal"};
  border-radius: ${Style.borderRadius};
  background-color: #fff;
  border: ${(props: any) =>
    props.embedded ? "0" : `1px solid ${borderColor(props)}`};
  border-top-left-radius: ${(props: any) =>
    props.embedded ? "0" : Style.borderRadius};
  border-top-right-radius: ${Style.borderRadius};
  border-bottom-left-radius: ${(props: any) =>
    props.embedded ? "0" : Style.borderRadius};
  border-bottom-right-radius: ${Style.borderRadius};
  color: ${Style.palette.black};
  padding: 0 8px 0 ${(props: any) => (props.embedded ? "0" : "16px")};
  font-size: inherit;
  transition: all ${Style.animations.swift};
  box-sizing: border-box;
  &:hover {
    border-color: ${(props: any) =>
      borderColor(
        props,
        props.disablePrimary ? undefined : Style.palette.primaryHighlight,
      )};
  }
  &:focus {
    border-color: ${(props: any) =>
      borderColor(
        props,
        props.disablePrimary ? undefined : Style.palette.primaryHighlight,
      )};
    outline: none;
  }
  &:disabled {
    color: ${Style.palette.grayscale[3]};
    border-color: ${Style.palette.grayscale[0]};
    background-color: ${Style.palette.grayscale[0]};
  }
  &::placeholder {
    color: ${Style.palette.grayscale[3]};
  }
`;
export const TextInputClose = styled.div<{ show?: boolean }>`
  display: ${props => (props.show ? "block" : "none")};
  width: 16px;
  height: 16px;
  background: url("${closeImage}") center no-repeat;
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
`;

interface Props {
  _ref?: (ref: HTMLElement) => void;
  disabled?: boolean;
  highlight?: boolean;
  large?: boolean;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: string;
  value?: string;
  showClose?: boolean;
  onCloseClick?: () => void;
  embedded?: boolean;
  height?: string;
  width?: string;
  lineHeight?: string;
  disablePrimary?: boolean;
  name?: string;
  style?: React.CSSProperties;
  autoComplete?: string;
  showPasswordStrength?: boolean;
  onPasswordStrengthChangeScore?: PasswordStrengthBarProps["onChangeScore"];
}
const TextInput = (props: Props) => {
  const { _ref, value, onChange, showClose, type } = props;
  const { onCloseClick, onPasswordStrengthChangeScore, ...inputProps } = props;
  let input: HTMLInputElement | null = null;
  return (
    <Wrapper>
      <Input
        {...inputProps}
        type={type || "text"}
        ref={ref => {
          input = ref;
          if (_ref && ref) {
            _ref(ref);
          }
        }}
        value={value}
        onChange={e => {
          if (onChange) {
            onChange(e.target.value);
          }
        }}
      />
      {props.type === "password" && props.showPasswordStrength ? (
        <PasswordStrengthBar
          password={value}
          onChangeScore={onPasswordStrengthChangeScore}
        />
      ) : null}
      <TextInputClose
        show={showClose && value !== "" && value !== undefined}
        onClick={() => {
          if (input) input.focus();
          if (onChange) onChange("");
          if (onCloseClick) onCloseClick();
        }}
      />
    </Wrapper>
  );
};

export default TextInput;
